import { useCallback, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CopyButton,
  Group,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconCopy, IconRefresh } from '@/components/icons'
import { PageCenterLoader } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import type { DebugMozgobotPipelineReportResponse, DebugPurgeAiQueueResponse, ProjectRow } from '@/types/api'

type Props = {
  projects: ProjectRow[]
  defaultHours?: number
}

export function DebugMozgobotReportTab({ projects, defaultHours = 1 }: Props) {
  const [hours, setHours] = useState(defaultHours)
  const [projectId, setProjectId] = useState('')
  const [sampleLimit, setSampleLimit] = useState(40)
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<DebugMozgobotPipelineReportResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [purging, setPurging] = useState(false)
  const [purgeResult, setPurgeResult] = useState<DebugPurgeAiQueueResponse | null>(null)

  const aiQueueDepth = report?.celery_queues?.ai ?? 0

  const projectOptions = [
    { value: '', label: 'Все проекты с AI' },
    ...projects.map((p) => ({ value: p.id, label: p.name })),
  ]

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.debugMozgobotPipelineReport({
        hours,
        sample_limit: sampleLimit,
        project_id: projectId || undefined,
      })
      setReport(res)
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Не удалось загрузить отчёт'
      setError(msg)
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [hours, projectId, sampleLimit])

  const text = report?.text || ''

  const purgeQueue = useCallback(async () => {
    const depth = report?.celery_queues?.ai ?? 0
    const ok = window.confirm(
      `Очистить Celery-очередь ai (${depth} задач)?\n\n` +
        'Сбросятся Redis dispatch-claim и очередь. Ждущие обращения будут поставлены заново (до 80 шт.).',
    )
    if (!ok) return
    setPurging(true)
    setPurgeResult(null)
    try {
      const res = await api.debugPurgeAiQueue({
        confirm: true,
        requeue_waiting_hours: hours,
        max_requeue: 80,
        release_dispatch_claims: true,
      })
      setPurgeResult(res)
      showNotification({
        color: 'teal',
        title: 'Очередь ai очищена',
        message: `Удалено ${res.purged_celery_ai_tasks ?? 0} задач, re-queue: ${res.requeued_tickets ?? 0}`,
      })
      await load()
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Не удалось очистить очередь'
      showNotification({ color: 'red', message: msg })
    } finally {
      setPurging(false)
    }
  }, [hours, load, report?.celery_queues?.ai])

  return (
    <Stack gap="sm">
      <Alert variant="light" color="blue" title="Отчёт для поддержки">
        <Text size="xs">
          Нажмите «Обновить», затем «Скопировать отчёт» и отправьте текст в чат — там конфиг, Redis,
          очереди Celery, воркеры и список зависших обращений на Мозгоботе с флагами блокировок.
        </Text>
      </Alert>

      {aiQueueDepth >= 500 ? (
        <Alert variant="filled" color="red" title="Критическое переполнение очереди ai">
          <Stack gap="xs">
            <Text size="xs">
              В Celery-очереди <strong>{aiQueueDepth}</strong> задач при ~12 слотах inference (4 воркера × 3).
              Новые обращения стоят в хвосте и могут не отвечать часами.
            </Text>
            <Button size="xs" color="red" variant="white" loading={purging} onClick={() => void purgeQueue()}>
              Очистить очередь ai и re-queue ждущих
            </Button>
          </Stack>
        </Alert>
      ) : null}

      {purgeResult ? (
        <Alert color="teal" title="Результат очистки">
          <Text size="xs">
            Удалено из ai: {purgeResult.purged_celery_ai_tasks ?? 0}, dispatch снято:{' '}
            {purgeResult.released_dispatch_claims ?? 0}, re-queue: {purgeResult.requeued_tickets ?? 0}, глубина ai
            после: {purgeResult.celery_ai_depth_after ?? 0}
          </Text>
        </Alert>
      ) : null}

      <Card withBorder padding="sm" radius="md">
        <Group align="flex-end" wrap="wrap" gap="sm">
          <NativeSelect
            size="xs"
            label="Проект"
            data={projectOptions}
            value={projectId}
            onChange={(e) => setProjectId(e.currentTarget.value)}
            maw={280}
            disabled={loading}
          />
          <NumberInput
            size="xs"
            label="Ждут ответа ≥ часов"
            value={hours}
            onChange={(v) => setHours(typeof v === 'number' ? v : 1)}
            min={1}
            max={168}
            w={130}
            disabled={loading}
          />
          <NumberInput
            size="xs"
            label="Примеров / проект"
            value={sampleLimit}
            onChange={(v) => setSampleLimit(typeof v === 'number' ? v : 40)}
            min={5}
            max={100}
            w={130}
            disabled={loading}
          />
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} loading={loading} onClick={() => void load()}>
            Обновить
          </Button>
          <CopyButton value={text} timeout={1500}>
            {({ copied, copy }) => (
              <Button
                size="xs"
                variant="filled"
                leftSection={<IconCopy size={14} />}
                disabled={!text || loading}
                color={copied ? 'teal' : 'blue'}
                onClick={() => {
                  copy()
                  showNotification({
                    color: 'teal',
                    message: copied ? 'Скопировано' : 'Отчёт скопирован в буфер',
                  })
                }}
              >
                {copied ? 'Скопировано' : 'Скопировать отчёт'}
              </Button>
            )}
          </CopyButton>
        </Group>
      </Card>

      {error ? (
        <Alert color="red" title="Ошибка">
          {error}
        </Alert>
      ) : null}

      {loading && !report ? <PageCenterLoader label="Сбор отчёта…" minHeight={120} /> : null}

      {report ? (
        <Card withBorder padding="sm" radius="md">
          <Stack gap="xs">
            <Group gap="md" wrap="wrap">
              <Text size="xs">
                Сгенерировано: <strong>{report.generated_at ? new Date(report.generated_at).toLocaleString('ru-RU') : '—'}</strong>
              </Text>
              <Text size="xs">
                Ждут ответа: <strong>{report.summary?.total_waiting_sampled ?? 0}</strong>
              </Text>
              <Text size="xs">
                Stale recovery: <strong>{report.summary?.total_stale_recovery ?? 0}</strong>
              </Text>
              <Text size="xs">
                Stale ai_released на Мозгоботе: <strong>{report.summary?.with_stale_released_marker ?? 0}</strong>
              </Text>
              <Text size="xs">
                Очередь Celery ai: <strong>{report.celery_queues?.ai ?? 0}</strong>
              </Text>
            </Group>
            <Textarea
              readOnly
              autosize
              minRows={16}
              maxRows={36}
              value={text}
              styles={{ input: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 } }}
            />
          </Stack>
        </Card>
      ) : null}
    </Stack>
  )
}
